import {
  DiagramNodeColor,
  DiagramNodeDisplay,
  DiagramNodeIconShape,
  DiagramNodeShape,
} from '@/components/diagrams/diagramStyle';
import { DiagramNodeKind } from '@/types/schema';

import { type NewDiagramNode } from '../../utils/diagramEdits';

/**
 * The canvas tools of the diagram builder editor. The current tool sets what
 * a click on the empty canvas does.
 */
export const DiagramEditorTool = {
  /** Select, move, and resize elements. A canvas click clears the selection. */
  Select: 'select',
  /** Place an entity tile with a title, a subtitle, notes, and a footer label. */
  Tile: 'tile',
  /** Place an entity that shows as an icon above its title. */
  Icon: 'icon',
  /** Place a filled shape above its label. */
  Shape: 'shape',
  /** Place standalone text: a note card with Markdown notes. */
  Text: 'text',
  /**
   * Add a stage divider across the canvas at the click height. The stage
   * that contains the click splits in two.
   */
  StageDivider: 'stageDivider',
} as const;
export type DiagramEditorTool =
  (typeof DiagramEditorTool)[keyof typeof DiagramEditorTool];

/** The tools that place one element at the next canvas click. */
export type DiagramPlacementTool = Exclude<
  DiagramEditorTool,
  typeof DiagramEditorTool.Select
>;

/** The tools that add one node at the next canvas click. */
export type DiagramNodeTool = Exclude<
  DiagramPlacementTool,
  typeof DiagramEditorTool.StageDivider
>;

/** The tools that the quick-add menu at the end of a dropped line offers. */
export type DiagramQuickAddTool =
  | typeof DiagramEditorTool.Tile
  | typeof DiagramEditorTool.Icon;

export function isPlacementTool(
  tool: DiagramEditorTool
): tool is DiagramPlacementTool {
  return tool !== DiagramEditorTool.Select;
}

export function isNodeTool(tool: DiagramEditorTool): tool is DiagramNodeTool {
  return isPlacementTool(tool) && tool !== DiagramEditorTool.StageDivider;
}

/** The node that each placement tool adds. */
export const NEW_NODE_BY_TOOL: Record<DiagramNodeTool, NewDiagramNode> = {
  [DiagramEditorTool.Tile]: {
    kind: DiagramNodeKind.Entity,
    label: 'New tile',
    nodeStyle: {
      display: DiagramNodeDisplay.Tile,
      color: DiagramNodeColor.Primary,
    },
  },
  [DiagramEditorTool.Icon]: {
    kind: DiagramNodeKind.Entity,
    label: 'New icon',
    nodeStyle: {
      display: DiagramNodeDisplay.TileIcon,
      iconShape: DiagramNodeIconShape.Square,
      color: DiagramNodeColor.Primary,
    },
  },
  [DiagramEditorTool.Shape]: {
    kind: DiagramNodeKind.Shape,
    label: 'New shape',
    nodeStyle: {
      shape: DiagramNodeShape.Circle,
      color: DiagramNodeColor.Primary,
    },
  },
  // Standalone text keeps all of its text in its notes. It has no title.
  [DiagramEditorTool.Text]: {
    kind: DiagramNodeKind.Text,
    label: '',
    nodeStyle: null,
  },
};
